import { RepositoryFactory } from '@/repositories/RepositoryFactory';
import { makeToastFaile, makeToastSuccess, unMaskPrice, catchNum} from '@/utils/common';
import max from 'lodash/max';
import min from 'lodash/min';

const ApprovalConfigRepository = RepositoryFactory.get('approvalConfigs');

/**
 * Service for handling approval config API calls
 */
export default {
  /**
   * Fetch approval configs with pagination and filtering
   *
   * @param {Object} params - Query parameters
   * @returns {Promise} - Promise with the response data
   */
  async fetchApprovalConfigs(params) {
    try {
      const responseData = await ApprovalConfigRepository.fetchAll(params);
      return {
        totalPages: responseData.totalPage,
        totalItems: responseData.totalRow,
        items: responseData.dataset.map((item, index) => ({
          ...item,
          count: index + 1,
        })),
      };
    } catch (error) {
      console.error('Error fetching approval configs:', error);
      makeToastFaile('Không thể tải danh sách cấu hình duyệt chi');
      throw error;
    }
  },

  /**
   * Delete an approval config
   *
   * @param {string|number} id - ID of the approval config to delete
   * @returns {Promise} - Promise with the response data
   */
  async deleteApprovalConfig(id) {
    try {
      const data = await ApprovalConfigRepository.delete(id);
      if (data.status === 200) {
        makeToastSuccess(data.message);
        return true;
      } else {
        makeToastFaile(data.message);
        return false;
      }
    } catch (error) {
      console.error('Error deleting approval config:', error);
      makeToastFaile('Không thể xóa cấu hình duyệt chi');
      throw error;
    }
  },

  /**
   * Get approval config by ID
   *
   * @param {string|number} id - ID of the approval config
   * @returns {Promise} - Promise with the response data
   */
  async getApprovalConfigById(id) {
    try {
      const configData = await ApprovalConfigRepository.getById(id);

      // Transform details to include ownerNames
      const details = configData.details.map((detail) => {
        const ownerNames = detail.owners.map((owner) => owner.name);
        return {
          ...detail,
          ownerNames: ownerNames.join(', '),
        };
      });

      return {
        ...configData,
        details,
      };
    } catch (error) {
      console.error('Error fetching approval config:', error);
      makeToastFaile('Không thể tải thông tin cấu hình duyệt chi');
      throw error;
    }
  },

  /**
   * Create or update an approval config
   *
   * @param {Object} config - The approval config data
   * @param {boolean} isUpdate - Whether this is an update operation
   * @returns {Promise} - Promise with the response data
   */
  async saveApprovalConfig(config, isUpdate) {
    try {
      const data = isUpdate
        ? await ApprovalConfigRepository.update(config)
        : await ApprovalConfigRepository.create(config);

      if (data.status === 200) {
        makeToastSuccess(data.message);
        return {
          success: true,
          message: data.message,
        };
      } else {
        makeToastFaile(data.message);
        return {
          success: false,
          message: data.message,
        };
      }
    } catch (error) {
      let errorMessage = 'Lỗi khi lưu cấu hình duyệt chi';

      if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        errorMessage = error.response.data.message;
      } else if (error.$error) {
        errorMessage = error.$error;
      }

      makeToastFaile(errorMessage);
      return {
        success: false,
        message: errorMessage,
      };
    }
  },

  /**
   * Fetch employees for approval config
   *
   * @returns {Promise} - Promise with the employee data
   */
  async fetchEmployees() {
    try {
      const employeeRepository = RepositoryFactory.get('employees');
      const employees = await employeeRepository.fetchListFilter();
      return employees.map((employee) => ({
        id: employee.id,
        name: employee.name,
      }));
    } catch (error) {
      console.error('Error fetching employees:', error);
      makeToastFaile('Không thể tải danh sách nhân viên');
      throw error;
    }
  },

  /**
   * Validate approval config details
   *
   * @param {Array} details - The config details to validate
   * @param {number} numOfStep - The number of steps
   * @returns {boolean} - Whether the details are valid
   */
  validateConfigDetails(details, numOfStep) {
    // Basic validation
    if (!details || !details.length) {
      makeToastFaile('Vui lòng nhập điều kiện duyệt chi!');
      return false;
    }

    // Step validation
    const steps = details.map((detail) => Number(detail.step));
    const maxStep = max(steps);
    const minStep = min(steps);

    if (minStep !== 1 || maxStep !== numOfStep) {
      const rangeStep = numOfStep === 1 ? '[1]' : `[1-${numOfStep}]`;
      makeToastFaile(`Vui lòng nhập thứ tự bước hợp lệ: ${rangeStep}!`);
      return false;
    }

    // Owner validation
    const nonOwnerStep = details.find(
      (detail) => !detail.ownerIds || !detail.ownerIds.length,
    );
    if (nonOwnerStep) {
      makeToastFaile(
        `Vui lòng chọn người duyệt cho bước: ${nonOwnerStep.step}!`,
      );
      return false;
    }

    return true;
  },

  /**
   * Get approval config by type
   *
   * @param {number} type - The approval type
   * @returns {Promise} - Promise with the response data
   */
  async getApprovalConfigByType(type) {
    try {
      return await ApprovalConfigRepository.getByType(type);
    } catch (error) {
      console.error('Error fetching approval config by type:', error);
      makeToastFaile('Không thể tải cấu hình duyệt chi theo loại');
      throw error;
    }
  },

  /**
   * Prepare approval config payload for API
   *
   * @param {Object} formData - The form data
   * @returns {Object} - The prepared payload
   */
  prepareConfigPayload(formData) {
    const details = formData.details.map((detail) => {
      const ownerIds = detail.owners.map((owner) => owner.id);

      return {
        id: detail.id,
        operator1: Number(detail.operator1),
        amount1: catchNum(unMaskPrice(detail.amount1)),
        operator2: Number(detail.operator2),
        amount2: catchNum(unMaskPrice(detail.amount2)),
        step: catchNum(detail.step),
        ownerType: Number(detail.ownerType),
        ownerIds: ownerIds,
        deleted: 0,
      };
    });

    return {
      id: formData.id,
      name: formData.name.trim(),
      approvalType: formData.approvalType,
      numOfStep: Number(formData.numOfStep),
      details: details,
      isActive: Boolean(formData.isActive),
    };
  },
};
